import { Typography } from '@euler/app/components/typography';
import { campaignStatusColor } from '@euler/app/flows/ai-suite/chat/AiCampaignMonitor';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { apdmPercent } from '@euler/functions/apdm/campaignFunnel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ApdmCampaignSummary, ApdmDashboardCampaign } from '@euler/model/apdm';
import dayjs from 'dayjs';
import { memo } from 'react';
import { View } from 'react-native';
import { ApdmCard, ApdmStatusPill, ApdmTag } from '../components/ApdmSurface';

/** A campaign's dates, e.g. `10月17日 – 11月16日`. */
export function apdmCampaignDates(summary: ApdmCampaignSummary): string {
  const start = summary.startAt ?? summary.createdAt;
  const format = (value: string) => dayjs(value).format('M月D日');
  return summary.endAt
    ? `${format(start)} – ${format(summary.endAt)}`
    : `${format(start)} 起`;
}

export const DashboardCampaignCard = memo(
  ({ item, onPress }: { item: ApdmDashboardCampaign; onPress: () => void }) => {
    const { rpx } = useResponsiveContext();
    const { summary, funnel } = item;
    const color = campaignStatusColor(summary.status);
    const share = funnel.audience
      ? Math.min(1, item.attempted / funnel.audience)
      : 0;
    const figure = (value: string, label: string) => (
      <View style={{ flex: 1, alignItems: 'center' }}>
        <Typography.Text bold size={rpx(14)} color={aiColors.foreground}>
          {value}
        </Typography.Text>
        <Typography.Text size={rpx(10)} color={aiColors.mutedForeground}>
          {label}
        </Typography.Text>
      </View>
    );
    return (
      <ApdmCard
        onPress={onPress}
        accessibilityLabel={summary.title}
        style={{ padding: rpx(16), gap: rpx(12) }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: rpx(8),
          }}
        >
          <View style={{ flex: 1 }}>
            <Typography.Text
              medium
              size={rpx(14)}
              color={aiColors.foreground}
              numberOfLines={2}
            >
              {summary.title}
            </Typography.Text>
            <Typography.Text
              size={rpx(11)}
              color={aiColors.mutedForeground}
              style={{ marginTop: rpx(2) }}
            >
              {apdmCampaignDates(summary)}
            </Typography.Text>
          </View>
          <ApdmStatusPill status={summary.status} />
        </View>
        <View>
          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
              已发送 {item.attempted.toLocaleString('zh-CN')}/
              {funnel.audience.toLocaleString('zh-CN')}
            </Typography.Text>
            <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
              {Math.round(share * 100)}%
            </Typography.Text>
          </View>
          <View
            style={{
              marginTop: rpx(6),
              height: rpx(6),
              borderRadius: rpx(3),
              backgroundColor: aiColors.muted,
              overflow: 'hidden',
            }}
          >
            <View
              style={{
                width: `${share * 100}%`,
                height: '100%',
                borderRadius: rpx(3),
                backgroundColor: color,
              }}
            />
          </View>
        </View>
        <View style={{ flexDirection: 'row', gap: rpx(6) }}>
          <ApdmTag text={`新线索 ${item.newLeads}`} color={aiColors.green} />
          <ApdmTag text={`回复 ${funnel.replied}`} color={aiColors.primary} />
        </View>
        <View
          style={{
            flexDirection: 'row',
            borderTopWidth: 1,
            borderTopColor: aiColors.border,
            paddingTop: rpx(12),
          }}
        >
          {figure(apdmPercent(funnel.replied, funnel.reached), '回复率')}
          {figure(
            funnel.reached ? funnel.converted.toLocaleString('zh-CN') : '—',
            '转化',
          )}
        </View>
      </ApdmCard>
    );
  },
);
